"use client"

import { useState, useEffect } from "react"
import { useAuth } from "../../../contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { MagnifyingGlassIcon, UserIcon, PencilIcon, CheckIcon, XMarkIcon, BanknotesIcon } from "@heroicons/react/24/outline"

export default function MetaPricingPage() {
  const { user, getAllUsersPricing, setUserPricing, setUserBilling, fetchCampaignHistory } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 })
  const [selectedUser, setSelectedUser] = useState(null)
  const [history, setHistory] = useState([])
  const [historySummary, setHistorySummary] = useState({})
  const [historyPagination, setHistoryPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 })
  const [historyPage, setHistoryPage] = useState(1)
  const [historyLimit] = useState(10)
  const [editingPricing, setEditingPricing] = useState({})
  const [defaultPricing, setDefaultPricing] = useState({})

  useEffect(() => {
    // fetch on mount and whenever page or search changes
    const t = setTimeout(() => fetchAllUsersPricing(), 300) // debounce search
    return () => clearTimeout(t)
  }, [page, searchTerm])

  useEffect(() => {
    if (!["superadmin", "admin"].includes(user?.role)) return
    loadHistory()
  }, [selectedUser, historyPage, user?.role])

  const fetchAllUsersPricing = async () => {
    try {
      setLoading(true)
      const result = await getAllUsersPricing(page, limit, searchTerm)

      if (result.success) {
        setUsers(result.users || [])
        setDefaultPricing(result.defaultPricing || {})
        setPagination(result.pagination || { currentPage: page, totalPages: 1 })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to fetch users pricing",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("Error fetching users pricing:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch users pricing",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    if (!["superadmin", "admin"].includes(user?.role)) return

    try {
      setHistoryLoading(true)
      const result = await fetchCampaignHistory(selectedUser?.userId || null, {}, historyPage, historyLimit)
      if (result.success) {
        setHistory(result.history || [])
        setHistorySummary(result.summary || {})
        setHistoryPagination(result.pagination || { currentPage: historyPage, totalPages: 1, totalRecords: 0 })
      }
    } catch (error) {
      console.error("Error loading meta history:", error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const selectHistoryUser = (targetUser = null) => {
    setSelectedUser(targetUser)
    setHistoryPage(1)
  }

  const handleSetPricing = async (userId, messageType, price) => {
    try {
      if (!price || isNaN(Number.parseFloat(price))) {
        Swal.fire({
          icon: "error",
          title: "Invalid Price",
          text: "Please enter a valid price",
          confirmButtonColor: "#4A7C59",
        })
        return
      }

      const result = await setUserPricing(userId, {
        [messageType]: Number.parseFloat(price),
      })

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `${messageType} pricing updated successfully`,
          confirmButtonColor: "#4A7C59",
        })
        fetchAllUsersPricing()
        setEditingPricing({})
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to update pricing",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("Error setting pricing:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update pricing",
        confirmButtonColor: "#4A7C59",
      })
    }
  }

  const startEditing = (userId, messageType, currentPrice) => {
    setEditingPricing({
      [`${userId}-${messageType}`]: currentPrice.toString(),
    })
  }

  const cancelEditing = (userId, messageType) => {
    const newEditing = { ...editingPricing }
    delete newEditing[`${userId}-${messageType}`]
    setEditingPricing(newEditing)
  }

  const saveEditing = (userId, messageType) => {
    const key = `${userId}-${messageType}`
    const price = editingPricing[key]
    if (price && !isNaN(Number.parseFloat(price))) {
      handleSetPricing(userId, messageType, price)
    }
  }

  const formatCredits = (value) => {
    const num = Number(value || 0)
    if (Number.isNaN(num)) return value || "0"
    return num.toFixed(5).replace(/\.0+$/, "").replace(/(\d\.\d*?[1-9])0+$/, "$1")
  }

  const formatDate = (date) => (date ? new Date(date).toLocaleString("en-IN") : "-")

  const filteredUsers = users
  const isReseller = user?.role === "reseller"

  if (!["superadmin", "admin", "reseller"].includes(user?.role)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                Meta Pricing Management
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Set custom pricing for marketing, utility, and authentication messages
              </p>
            </div>
            {["superadmin", "admin"].includes(user?.role) && (
              <button
                type="button"
                onClick={() => navigate("/recharge")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700"
              >
                <BanknotesIcon className="h-5 w-5" />
                Balance
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(1)
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          {!isReseller && (
            <button
              type="button"
              onClick={() => selectHistoryUser(null)}
              className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
                selectedUser
                  ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  : "bg-green-600 text-white border-green-600"
              }`}
            >
              All History
            </button>
          )}
        </div>

        {/* Default Pricing Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Default Pricing</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing.marketing || "0.85"}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Marketing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing.utility || "0.15"}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Utility</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing.authentication || "0.15"}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Authentication</div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">User</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Role</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Marketing</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Utility</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Authentication</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Custom Pricing</th>
                  {!isReseller && (
                    <>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Deduct On Failed</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Show Credits</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Failed Cap Enabled</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Failed Cap %</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Refund Failed Credit</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Refund Delay (hrs)</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">History</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={isReseller ? "6" : "13"} className="py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={isReseller ? "6" : "13"} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      <UserIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr
                      key={u.userId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                            <UserIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">{u.name || "N/A"}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.role === "superadmin"
                              ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                              : u.role === "admin"
                                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                : u.role === "reseller"
                                  ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                  : u.role === "company"
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                        </span>
                      </td>
                      {["marketing", "utility", "authentication"].map((messageType) => {
                        const editKey = `${u.userId}-${messageType}`
                        const isEditing = editingPricing.hasOwnProperty(editKey)
                        const currentPrice = u.pricing?.[messageType] || defaultPricing[messageType] || 0
                        const isCustom = u.customPricing?.some((cp) => cp.messageType === messageType)

                        return (
                          <td key={messageType} className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              {isEditing ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editingPricing[editKey]}
                                    onChange={(e) =>
                                      setEditingPricing({
                                        ...editingPricing,
                                        [editKey]: e.target.value,
                                      })
                                    }
                                    className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                                  />
                                  <button
                                    onClick={() => saveEditing(u.userId, messageType)}
                                    className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => cancelEditing(u.userId, messageType)}
                                    className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`font-medium ${
                                      isCustom ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-white"
                                    }`}
                                  >
                                    ₹{currentPrice}
                                  </span>
                                  <button
                                    onClick={() => startEditing(u.userId, messageType, currentPrice)}
                                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>
                                  {isCustom && (
                                    <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full">
                                      Custom
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        )
                      })}
                      <td className="py-4 px-6">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.hasCustomPricing
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {u.hasCustomPricing ? "Yes" : "No"}
                        </span>
                      </td>
                      {!isReseller && (
                        <>
                          <td className="py-4 px-6">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={u.billing?.deductOnFailed ?? true}
                                onChange={async (e) => {
                                  const newVal = e.target.checked
                                  const oldBilling = u.billing || { deductOnFailed: true, showCredits: true }
                                  u.billing = { ...oldBilling, deductOnFailed: newVal }
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                  const res = await setUserBilling(u.userId, { deductOnFailed: newVal })
                                  if (!res.success) {
                                    Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                    u.billing = oldBilling
                                    setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                  }
                                }}
                                className="form-checkbox h-4 w-4 text-green-600"
                              />
                            </label>
                          </td>
                          <td className="py-4 px-6">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={u.billing?.showCredits ?? true}
                                onChange={async (e) => {
                                  const newVal = e.target.checked
                                  const oldBilling = u.billing || { deductOnFailed: true, showCredits: true }
                                  u.billing = { ...oldBilling, showCredits: newVal }
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                  const res = await setUserBilling(u.userId, { showCredits: newVal })
                                  if (!res.success) {
                                    Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                    u.billing = oldBilling
                                    setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                  }
                                }}
                                className="form-checkbox h-4 w-4 text-green-600"
                              />
                            </label>
                          </td>
                          <td className="py-4 px-6">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={u.reporting?.failedCapEnabled ?? false}
                                onChange={async (e) => {
                                  const newVal = e.target.checked
                                  const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                  u.reporting = { ...oldReporting, failedCapEnabled: newVal }
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                                  const res = await setUserBilling(u.userId, { failedCapEnabled: newVal })
                                  if (!res.success) {
                                    Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                    u.reporting = oldReporting
                                    setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: oldReporting } : p)))
                                  }
                                }}
                                className="form-checkbox h-4 w-4 text-green-600"
                              />
                            </label>
                          </td>
                          <td className="py-4 px-6">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={u.reporting?.failedCapPercent ?? 0}
                              onChange={(e) => {
                                const val = Number.parseInt(e.target.value || 0)
                                const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                u.reporting = { ...oldReporting, failedCapPercent: isNaN(val) ? 0 : val }
                                setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                              }}
                              onBlur={async (e) => {
                                const val = Number.parseInt(e.target.value || 0)
                                const sanitized = isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
                                const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                u.reporting = { ...oldReporting, failedCapPercent: sanitized }
                                setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                                const res = await setUserBilling(u.userId, { failedCapPercent: sanitized })
                                if (!res.success) {
                                  Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                  u.reporting = oldReporting
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: oldReporting } : p)))
                                }
                              }}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                            />
                          </td>
                          <td className="py-4 px-6">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={u.billing?.failedRefundEnabled ?? false}
                                onChange={async (e) => {
                                  const newVal = e.target.checked
                                  const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                  u.billing = { ...oldBilling, failedRefundEnabled: newVal }
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                  const res = await setUserBilling(u.userId, { failedRefundEnabled: newVal })
                                  if (!res.success) {
                                    Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                    u.billing = oldBilling
                                    setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                  }
                                }}
                                className="form-checkbox h-4 w-4 text-green-600"
                              />
                            </label>
                          </td>
                          <td className="py-4 px-6">
                            <input
                              type="number"
                              min="1"
                              max="168"
                              step="1"
                              value={u.billing?.failedRefundHours ?? 24}
                              onChange={(e) => {
                                const val = Number.parseInt(e.target.value || 24)
                                const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                u.billing = { ...oldBilling, failedRefundHours: isNaN(val) ? 24 : val }
                                setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                              }}
                              onBlur={async (e) => {
                                const val = Number.parseInt(e.target.value || 24)
                                const sanitized = isNaN(val) ? 24 : Math.max(1, Math.min(168, val))
                                const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                u.billing = { ...oldBilling, failedRefundHours: sanitized }
                                setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                const res = await setUserBilling(u.userId, { failedRefundHours: sanitized })
                                if (!res.success) {
                                  Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                  u.billing = oldBilling
                                  setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                }
                              }}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                            />
                          </td>
                          <td className="py-4 px-6">
                            <button
                              type="button"
                              onClick={() => selectHistoryUser(u)}
                              className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                                selectedUser?.userId === u.userId
                                  ? "bg-green-600 text-white border-green-600"
                                  : "bg-white text-green-700 border-green-200 hover:bg-green-50"
                              }`}
                            >
                              View
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination controls */}
          <div className="flex items-center justify-between p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.currentPage <= 1}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >Prev</button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >Next</button>
            </div>
          </div>
        </div>

        {!isReseller && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedUser ? `${selectedUser.name || selectedUser.email || "User"} History` : "All Users History"}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-300">Charged</p>
                <p className="font-bold text-gray-900 dark:text-white">Rs. {formatCredits(historySummary.totalCharged || 0)}</p>
              </div>
              <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <p className="text-xs text-amber-700 dark:text-amber-300">Refunded</p>
                <p className="font-bold text-amber-800 dark:text-amber-200">Rs. {formatCredits(historySummary.totalRefunded || 0)}</p>
              </div>
              <div className="px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                <p className="text-xs text-green-700 dark:text-green-300">Refund Count</p>
                <p className="font-bold text-green-800 dark:text-green-200">{historySummary.refundCount || 0}</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Date & Time</th>
                  {!selectedUser && <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">User</th>}
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Recipient</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Status</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign Cut</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign Balance</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Refund</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">After Refund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {historyLoading ? (
                  <tr>
                    <td colSpan={selectedUser ? 8 : 9} className="py-10 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={selectedUser ? 8 : 9} className="py-10 text-center text-gray-500 dark:text-gray-400">
                      No history found
                    </td>
                  </tr>
                ) : (
                  history.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 whitespace-nowrap text-gray-900 dark:text-white">{formatDate(record.createdAt)}</td>
                      {!selectedUser && (
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 dark:text-white">{record.userName || "N/A"}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{record.userEmail || ""}</div>
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900 dark:text-white">{record.campaignName || "N/A"}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{record.templateName || ""}</div>
                      </td>
                      <td className="py-3 px-4 font-mono text-gray-900 dark:text-white">{record.recipientNumber || "-"}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            record.status === "refunded"
                              ? "bg-amber-100 text-amber-800"
                              : record.status === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {record.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">Rs. {formatCredits(record.chargeAmount || 0)}</td>
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">
                        {record.campaignBalanceBefore !== null && record.campaignBalanceBefore !== undefined ? (
                          <>
                            <div>Old: Rs. {formatCredits(record.campaignBalanceBefore)}</div>
                            <div>After: Rs. {formatCredits(record.campaignBalanceAfter)}</div>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-3 px-4 font-semibold text-amber-700 dark:text-amber-300">
                        {record.refundAmount ? `Rs. ${formatCredits(record.refundAmount)}` : "-"}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">
                        {record.status === "refunded" ? (
                          <>
                            <div>Before: Rs. {formatCredits(record.refundBalanceBefore)}</div>
                            <div>After: Rs. {formatCredits(record.refundBalanceAfter)}</div>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {historyPagination.currentPage || historyPage} of {historyPagination.totalPages || 1}
              {historyPagination.totalRecords ? ` (${historyPagination.totalRecords} records)` : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={(historyPagination.currentPage || historyPage) <= 1}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.min(historyPagination.totalPages || 1, p + 1))}
                disabled={(historyPagination.currentPage || historyPage) >= (historyPagination.totalPages || 1)}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
